import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { portalService } from '../../services/portal';
import type { InboxAlertsData } from '../../types/portal';
import styles from './InboxAlertBanners.module.css';

interface InboxAlertBannersProps {
  onViewUnmapped?: () => void;
}

/**
 * Alert banners displayed above the inbox stats grid.
 *
 * Shows warnings for:
 * 1. Unmapped emails — emails with no salesperson mapping (high urgency)
 * 2. Unassigned salespeople — active Sage codes not assigned to any user
 */
const InboxAlertBanners: React.FC<InboxAlertBannersProps> = ({ onViewUnmapped }) => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<InboxAlertsData | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const fetchAlerts = useCallback(async () => {
    try {
      const data = await portalService.getInboxAlerts();
      setAlerts(data);
    } catch (err) {
      console.error('Failed to fetch inbox alerts:', err);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  if (!alerts) return null;

  const showUnmapped =
    alerts.unmapped_email_count > 0 && !dismissed.has('unmapped');
  const showUnassigned =
    alerts.unassigned_salesperson_count > 0 && !dismissed.has('unassigned');

  if (!showUnmapped && !showUnassigned) return null;

  return (
    <div className={styles.container}>
      {showUnmapped && (
        <div className={styles.banner} data-variant="error">
          <div className={styles.bannerIcon}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div className={styles.bannerContent}>
            <strong>{alerts.unmapped_email_count} unmapped email{alerts.unmapped_email_count !== 1 ? 's' : ''}</strong>
            <span className={styles.bannerText}>
              {' '}could not be mapped to a salesperson &mdash; these need manual review
            </span>
          </div>
          {onViewUnmapped && (
            <button
              className={styles.actionBtn}
              onClick={onViewUnmapped}
            >
              View &amp; Fix
            </button>
          )}
          <button
            className={styles.dismissBtn}
            onClick={() => setDismissed((prev) => new Set(prev).add('unmapped'))}
            aria-label="Dismiss"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {showUnassigned && (
        <div className={styles.banner} data-variant="error">
          <div className={styles.bannerIcon}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <div className={styles.bannerContent}>
            <strong>
              {alerts.unassigned_salesperson_count} unassigned salesperson{alerts.unassigned_salesperson_count !== 1 ? 's' : ''}
            </strong>
            <span className={styles.bannerText}>
              {' '}({alerts.unassigned_salesperson_codes.join(', ')}) — emails from their customers won&apos;t appear in anyone&apos;s inbox
            </span>
          </div>
          <button
            className={styles.actionBtn}
            onClick={() => navigate('/portal/my-salespeople')}
          >
            Manage
          </button>
          <button
            className={styles.dismissBtn}
            onClick={() => setDismissed((prev) => new Set(prev).add('unassigned'))}
            aria-label="Dismiss"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default InboxAlertBanners;
